import React, { memo, useCallback } from "react";
import { useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";
import { myPush as push, selectList } from "../actions";
import imgLogoCropx from "../../img/cropx.svg";
import betaIcon from "../../img/beta-icon@2x.png";
import { config } from "../Config";
import i18n from "i18next";
import "./NavAside.scss";
import cx from "classnames";
import { useSSelector, accountFeatures, A } from "../util";
import { showSubscriptionModal } from "./Pro";

interface ItemProps {
  name: string;
  icon: string;
  children: React.ReactChildren;
  className?: string;
  needPro?: boolean;
  customIcon?: string;
  link?: string;
}

const Item = (props: ItemProps) => {
  const { name, icon, children, className, customIcon, needPro = false, link } = props;

  const dispatch = useDispatch();
  const isShow = name.startsWith("show_");
  const path = "/" + (isShow ? name.substring(5) : "");
  const { pathname } = useLocation();
  const active = isShow && pathname.length > 1 && pathname.startsWith(path);

  const features = useSSelector((s) => accountFeatures(s.activeUser.account));

  const onClick = useCallback(() => {
    if (needPro && !features.pro) {
      showSubscriptionModal(dispatch, "");
      return;
    }
    const changed = pathname !== path && !active;
    dispatch({ type: "DESELECT" });
    dispatch(selectList({ selectedList: null }));

    if (link) {
      window.open(link);
      return;
    }

    if (changed) {
      dispatch(push(path));
    } else if (pathname !== "/") {
      dispatch(push("/"));
    }
  }, [needPro, features, dispatch, active, path, pathname, link]);

  return (
    <li
      onClick={onClick}
      role="presentation"
      className={cx("btn-aside", className, { active: active })}
    >
      <div className="btn-content">
        {icon && <i className={`fal fa-${icon} font-size-2_2`} />}
        {customIcon && <img alt={name} src={customIcon} />}
        <span>{children}</span>
      </div>
    </li>
  );
};

export const NavAside = memo(() => {
  const [activeUser, searchFilter] = useSSelector((s) => [s.activeUser, s.searchFilter]);
  const loggedIn = activeUser.state === "loggedIn";
  const hasBetaEWSFeature = Boolean(activeUser.settings?.showEWS && config.bb_country === "NL");
  const premium = activeUser.account?.features?.premium;

  const contactsLink = `${config.DACOM_URL}/cropr/settings/farm_contacts/`;

  return (
    <aside className="mod_bb_nav_aside">
      <div className="inner">
        <ul id="nav_aside_nav" className="nav nav-tabs">
          <Item
            name="show_filter"
            icon="filter"
            className={`d-none d-md-block ifilter ${searchFilter ? " active" : ""}`}
          >
            {i18n.t("Filter")}
          </Item>
          <Item name="show_search" icon="search" className="d-block d-md-none">
            {i18n.t("Search")}
          </Item>
          {loggedIn && (
            <>
              <Item key="bunder" name="show_lists" icon="clipboard-list-check" needPro>
                {i18n.t("Lists")}
              </Item>
              <Item key="farm" name="show_my_farm" icon="farm" needPro>
                {i18n.t("Farm")}
              </Item>
            </>
          )}
          {config.labs && (
            <>
              <Item
                name="show_dacom_settings"
                icon="cog"
                link={`${config.DACOM_URL}/cropr/settings/farm/`}
                className={!loggedIn ? "d-none" : "d-none d-md-block"}
              >
                {i18n.t("Settings")}
              </Item>
              <Item
                name="show_settings"
                className={!loggedIn ? "" : "d-none d-md-block"}
                icon={null}
                customIcon={betaIcon}
              >
                {i18n.t("Beta features")}
              </Item>
            </>
          )}
          {loggedIn && hasBetaEWSFeature && (
            <Item name="show_ews" icon="radar">
              {i18n.t("Radar")}
            </Item>
          )}
          {loggedIn && !hasBetaEWSFeature && config.bb_country === "NL" && (
            <li key="a" className="btn-aside d-block d-md-none" />
          )}
          {loggedIn && (
            <Item name="show_controls" icon="bars" className="d-block d-md-none">
              {i18n.t("Layers")}
            </Item>
          )}
          {premium && (
            <Item
              name="show_contacts"
              icon="users"
              className="btn-aside d-none d-md-block"
              link={contactsLink}
            >
              {i18n.t("Contacts")}
            </Item>
          )}
          {!loggedIn && <li key="b" className="btn-aside d-block d-md-none" />}
          {!loggedIn && <li key="c" className="btn-aside d-block d-md-none" />}
          {loggedIn && config.bb_country !== "NL" && (
            <li key="b" className="btn-aside d-block d-md-none" />
          )}
          {!loggedIn && (
            <Item className="d-none d-block d-md-none" name="close_panel" icon="map-marker-alt">
              {i18n.t("Map")}
            </Item>
          )}
        </ul>
      </div>
      <div className="nav corner">
        <A className="company_logo" href="https://www.cropx.com/">
          <img width="80" alt="CropX" src={imgLogoCropx} />
        </A>
      </div>
    </aside>
  );
});
